import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { priceChangeRequestService, PriceChangeRequest } from '../../services/priceChangeRequestService';

interface RequestPriceChangeModalProps {
  open: boolean;
  onClose: () => void;
  site: {
    id: string;
    site_url: string;
    base_price: number;
  };
  existingRequest?: PriceChangeRequest | null;
}

const RequestPriceChangeModal: React.FC<RequestPriceChangeModalProps> = ({
  open,
  onClose,
  site,
  existingRequest,
}) => {
  const queryClient = useQueryClient();
  const [requestedPrice, setRequestedPrice] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string>('');

  const isModify = existingRequest?.status === 'PENDING';
  const isReRequest = existingRequest?.status === 'REJECTED';
  const isNewAfterApproved = existingRequest?.status === 'APPROVED';

  // Pre-fill with existing request data when modal opens (except for approved - allow fresh request)
  useEffect(() => {
    if (open && existingRequest && !isNewAfterApproved) {
      setRequestedPrice(existingRequest.requestedPrice.toString());
      setReason(existingRequest.reason || '');
    } else if (open) {
      setRequestedPrice('');
      setReason('');
    }
  }, [open, existingRequest, isNewAfterApproved]);

  const createRequestMutation = useMutation({
    mutationFn: priceChangeRequestService.createRequest.bind(priceChangeRequestService),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-change-requests'] });
      handleClose();
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to submit price change request');
    },
  });

  const handleClose = () => {
    setRequestedPrice('');
    setReason('');
    setError('');
    onClose();
  };

  const handleSubmit = () => {
    setError('');

    // Validation
    const priceNum = parseFloat(requestedPrice);
    if (!requestedPrice || isNaN(priceNum) || priceNum <= 0) {
      setError('Please enter a valid price');
      return;
    }

    if (priceNum === site.base_price) {
      setError('New price must be different from current price');
      return;
    }

    createRequestMutation.mutate({
      siteId: site.id,
      requestedPrice: priceNum,
      reason: reason.trim() || undefined,
    });
  };

  const priceChange = requestedPrice
    ? priceChangeRequestService.calculatePriceChange(site.base_price, parseFloat(requestedPrice))
    : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isModify 
          ? 'Modify Price Change Request' 
          : isReRequest 
          ? 'Re-submit Price Change Request' 
          : isNewAfterApproved
          ? 'Request New Price Change'
          : 'Request Price Change'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Site Information */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Site URL
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {site.site_url}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
              Current Price
            </Typography>
            <Typography variant="h6" color="primary">
              ${site.base_price.toFixed(2)}
            </Typography>
          </Box>

          {/* Existing Request Info */}
          {existingRequest && (
            <Alert
              severity={
                existingRequest.status === 'REJECTED' 
                  ? 'error' 
                  : existingRequest.status === 'APPROVED'
                  ? 'success'
                  : 'warning'
              }
              sx={{ mb: 2 }}
              icon={false}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="body2" fontWeight="medium">
                  {existingRequest.status === 'APPROVED' ? 'Last Approved Request:' : 'Previous Request:'}
                </Typography>
                <Chip
                  label={existingRequest.status}
                  color={priceChangeRequestService.getStatusColor(existingRequest.status)}
                  size="small"
                />
              </Box>
              {existingRequest.status === 'APPROVED' ? (
                <>
                  <Typography variant="body2">
                    Old Price: ${existingRequest.currentPrice.toFixed(2)} → New Price: ${existingRequest.requestedPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                    ✓ Price updated successfully
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    Requested Price: ${existingRequest.requestedPrice.toFixed(2)}
                  </Typography>
                  {existingRequest.rejectionReason && (
                    <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                      Rejection Reason: {existingRequest.rejectionReason}
                    </Typography>
                  )}
                </>
              )}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Requested Price Input */}
          <TextField
            fullWidth
            label="Requested Price"
            type="number"
            value={requestedPrice}
            onChange={(e) => setRequestedPrice(e.target.value)}
            placeholder="Enter new price"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 0.01,
            }}
            sx={{ mb: 2 }}
            disabled={createRequestMutation.isPending}
          />

          {/* Price Change Indicator */}
          {priceChange && !isNaN(priceChange.amount) && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: priceChange.isIncrease ? 'success.lighter' : 'error.lighter',
                borderRadius: 1,
                border: 1,
                borderColor: priceChange.isIncrease ? 'success.main' : 'error.main',
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                Price Change: {priceChange.isIncrease ? '+' : ''}${priceChange.amount.toFixed(2)} (
                {priceChange.isIncrease ? '+' : ''}
                {priceChange.percentage.toFixed(1)}%)
              </Typography>
            </Box>
          )}

          {/* Reason Input */}
          <TextField
            fullWidth
            label="Reason (Optional)"
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you're requesting this price change..."
            disabled={createRequestMutation.isPending}
            helperText="Providing a reason helps Super Admin review your request faster"
          />

          {/* Info Alert */}
          <Alert severity="info" sx={{ mt: 2 }}>
            {isModify
              ? 'Your modified request will replace the current pending request.'
              : 'Your request will be reviewed by Super Admin. You\'ll be notified once it\'s approved or rejected.'
            }
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={createRequestMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createRequestMutation.isPending || !requestedPrice}
          startIcon={createRequestMutation.isPending && <CircularProgress size={20} />}
        >
          {createRequestMutation.isPending ? 'Submitting...' : isModify ? 'Update Request' : isReRequest ? 'Re-submit Request' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestPriceChangeModal;
